import React, { useCallback, useEffect, useState } from "react";
import { Mic, Plus, MoreHorizontal, Pencil, Trash2, StickyNote, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";
import VoiceRecorderPanel, { NOTE_CATEGORIES } from "@/components/voice/VoiceRecorderPanel";

const fmtDate = (iso, lang) => {
  try { return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return (iso || "").slice(0, 10); }
};
const fmtDur = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.round(s || 0) % 60).padStart(2, "0")}`;
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

export default function NotesTab({ player }) {
  const { tf, lang } = useLang();
  const [notes, setNotes] = useState(null);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [editor, setEditor] = useState(null); // null | {id?, title, category, content}
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("recent");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (cat !== "all") params.set("category", cat);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    params.set("sort", sort);
    api.get(`/players/${player.id}/notes?${params}`)
      .then((r) => setNotes(r.data.items || []))
      .catch(() => setNotes([]));
  }, [player.id, q, cat, dateFrom, dateTo, sort]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  const hasFilters = q.trim() || cat !== "all" || dateFrom || dateTo;

  const saveText = async () => {
    if (!editor?.content?.trim()) { toast.error(tf("Contenu requis")); return; }
    try {
      if (editor.id) {
        await api.patch(`/players/notes/${editor.id}`, editor);
      } else {
        await api.post(`/players/${player.id}/notes`, { ...editor, source_type: "text" });
      }
      toast.success(tf("Note enregistrée"));
      setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };

  const remove = async (id) => {
    try { await api.delete(`/players/notes/${id}`); toast.success(tf("Note supprimée")); load(); }
    catch { toast.error(tf("Erreur")); }
  };

  const removeAudio = async (id) => {
    try { await api.delete(`/players/notes/${id}/audio`); toast.success(tf("Audio supprimé")); load(); }
    catch { toast.error(tf("Erreur")); }
  };

  const changeRetention = async (id, days) => {
    try {
      const r = await api.patch(`/players/notes/${id}/audio/retention`, { retention_days: Number(days) });
      if (r.data?.deleted) toast.info(tf("Période déjà expirée — audio supprimé."));
      else toast.success(tf("Période de rétention mise à jour"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };

  return (
    <div className="space-y-4" data-testid="player-notes-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h3 className="text-sm font-semibold">{tf("Notes")}</h3>
          <p className="text-xs text-muted-foreground">{tf("Informations clés, réunions et observations.")}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" className="h-9 gap-1.5 bg-info hover:bg-info/90 text-white"
            onClick={() => setEditor({ title: "", category: "Général", content: "" })} data-testid="add-note-btn">
            <Plus className="h-3.5 w-3.5" /> {tf("Ajouter une note")}
          </Button>
          <Button size="sm" variant="outline" className="h-9 w-9 p-0 border-athlynx/40 text-athlynx hover:bg-athlynx/10"
            onClick={() => setRecorderOpen((v) => !v)} aria-label={tf("Enregistrer une note vocale")}
            title={tf("Enregistrer une note vocale")} data-testid="notes-voice-btn">
            <Mic className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2.5" data-testid="notes-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tf("Rechercher dans les notes…")}
            className="h-9 pl-8 text-sm" data-testid="notes-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-9 w-[140px]" data-testid="notes-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Toutes catégories")}</SelectItem>
            {NOTE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{tfr(c)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="h-9 w-[140px] text-xs" aria-label={tf("Du")} data-testid="notes-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="h-9 w-[140px] text-xs" aria-label={tf("Au")} data-testid="notes-date-to" />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="notes-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">{tf("Plus récentes")}</SelectItem>
            <SelectItem value="oldest">{tf("Plus anciennes")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {recorderOpen && (
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="notes-voice-recorder">
          <div className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Mic className="h-3.5 w-3.5 text-athlynx" /> {tf("Nouvelle note vocale")}
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {tf("Enregistrez votre note. Elle sera transcrite puis ajoutée au profil de")} {player.name}.
          </p>
          <VoiceRecorderPanel autoStart athlete={{ id: player.id, name: player.name }}
            onClose={() => setRecorderOpen(false)} onSaved={() => { setRecorderOpen(false); load(); }} />
        </div>
      )}

      {notes === null ? (
        <div className="text-xs text-muted-foreground">…</div>
      ) : notes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="notes-empty">
          {hasFilters ? tf("Aucune note ne correspond à votre recherche.") : tf("Aucune note pour le moment.")}
        </div>
      ) : (
        <div className="space-y-2" data-testid="notes-list">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl border border-border bg-card p-3" data-testid={`note-item-${n.id}`}>
              <div className="flex items-start gap-2">
                <StickyNote className="h-4 w-4 text-info mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{n.title}</span>
                    <Badge variant="outline" className="text-[10px] h-5">{tfr(n.category)}</Badge>
                    {n.source_type === "voice" && (
                      <Badge className="text-[10px] h-5 bg-athlynx/10 text-athlynx border-athlynx/25 gap-1" variant="outline">
                        <Mic className="h-2.5 w-2.5" /> {tf("Vocal")}
                      </Badge>
                    )}
                    <span className="text-[11px] text-muted-foreground ml-auto">{fmtDate(n.created_at, lang)}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                          aria-label={tf("Actions")} data-testid={`note-menu-${n.id}`}>
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditor({ id: n.id, title: n.title, category: n.category, content: n.content })}>
                          <Pencil className="h-3.5 w-3.5 mr-2" /> {tf("Modifier")}
                        </DropdownMenuItem>
                        {n.has_audio && (
                          <DropdownMenuItem onClick={() => removeAudio(n.id)} data-testid={`note-delete-audio-${n.id}`}>
                            <Mic className="h-3.5 w-3.5 mr-2" /> {tf("Supprimer l'audio maintenant")}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => remove(n.id)} className="text-danger focus:text-danger">
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> {tf("Supprimer")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-sm text-foreground/85 mt-1 whitespace-pre-wrap">{n.content}</p>
                  {n.has_audio && (
                    <div className="mt-2 rounded-lg border border-border bg-secondary/30 p-2 space-y-1.5" data-testid={`note-audio-box-${n.id}`}>
                      <audio controls preload="none" className="w-full h-8"
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/players/notes/${n.id}/audio`}
                        data-testid={`note-audio-${n.id}`} />
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground/80">{tf("Enregistrement original")}</span>
                        {n.duration_seconds ? <span>{tf("Durée")} : {fmtDur(n.duration_seconds)}</span> : null}
                        {n.audio_expires_at && (
                          <span data-testid={`note-audio-expiry-${n.id}`}>
                            {tf("Suppression programmée")} : {fmtDate(n.audio_expires_at, lang)} · {daysLeft(n.audio_expires_at)} {tf("jours restants")}
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-1.5">
                          <span>{tf("Rétention")}</span>
                          <Select value={String(n.retention_days || 30)} onValueChange={(v) => changeRetention(n.id, v)}>
                            <SelectTrigger className="h-7 w-[100px] text-[11px]" aria-label={tf("Supprimer automatiquement après")}
                              data-testid={`note-retention-select-${n.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="30">{tf("30 jours")}</SelectItem>
                              <SelectItem value="90">{tf("90 jours")}</SelectItem>
                              <SelectItem value="365">{tf("365 jours")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </span>
                      </div>
                    </div>
                  )}
                  {!n.has_audio && n.audio_deleted_at && (
                    <p className="text-[11px] text-muted-foreground mt-1.5 italic" data-testid={`note-audio-deleted-${n.id}`}>
                      {tf("Enregistrement original supprimé. Transcription conservée.")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="note-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.id ? tf("Modifier la note") : tf("Ajouter une note")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={editor?.title || ""} onChange={(e) => setEditor((s) => ({ ...s, title: e.target.value }))}
              placeholder={tf("Titre de la note")} className="h-9 text-sm" data-testid="note-title-input" />
            <Select value={editor?.category || "Général"} onValueChange={(v) => setEditor((s) => ({ ...s, category: v }))}>
              <SelectTrigger className="h-9" data-testid="note-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{NOTE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{tfr(c)}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea value={editor?.content || ""} onChange={(e) => setEditor((s) => ({ ...s, content: e.target.value }))}
              className="min-h-[100px] text-sm" placeholder={tf("Votre note…")} data-testid="note-content-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditor(null)}>{tf("Annuler")}</Button>
            <Button onClick={saveText} className="bg-info hover:bg-info/90 text-white" data-testid="note-save-btn">{tf("Enregistrer")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
